import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NotifierService } from 'angular-notifier';
import { GlobalApiService } from '../../services/global-api.service';
import * as moment from 'moment';
import {
  BatchScheduleEventEditType,
  PotEvent
} from '../../constants/constants';
import {
  BookedClass,
  findOverlappingClass
} from '../../shared/class-overlap.util';
import {
  addDaysUtc,
  addMinutesToTime,
  classWeekOf,
  formatClassWeek,
  formatDateLabel,
  formatDuration,
  formatTime12Hour,
  generateWeeklyOccurrences,
  getUtcDay,
  isPastDateTime,
  toViewerTime,
  viewerTimeZoneAbbr
} from '../../shared/class-schedule.util';

export interface ClassFormDialogData {
  startDate: string;
  endDate: string;
  startTime?: string;
  endTime?: string;
  pot?: PotEvent;
  duplicateOf?: PotEvent;
  // The batch's other classes, for the tutor clash check
  bookedClasses?: BookedClass[];
  // Edits one saved batch class (pot.bseId) through the event API
  classEventEditType?: BatchScheduleEventEditType;
  // The batch's dates (YYYY-MM-DD); a schedule's dates must fall inside them
  batchStartDate?: string;
  batchEndDate?: string;
  // Whole-schedule edit: the class that was clicked (YYYY-MM-DD)
  clickedDate?: string;
  // "This and following" edit: the saved classes the edit applies to
  seriesRange?: { start: string; end: string; count: number };
}

export interface ClassFormSchedule {
  bschId: number | null;
  title: string;
  // YYYY-MM-DD here; sent to the API as MM-DD-YYYY
  scheduleStartDate: string;
  scheduleEndDate: string;
  type: number;
  subject: number;
  tutor: string;
  day: number;
  startTime: string;
  endTime: string;
  timeZone: number;
  classUrl: string;
  isScheduleActive: number;
  isDisabled: number;
  // 1 = every week, 2 = alternate weeks, 3 = every 3 weeks, 4 = every 4 weeks
  repeatFrequency: number;
}

export type ClassFormResult =
  | { saved: true; schedules: ClassFormSchedule[] }
  | undefined;

const MAX_OCCURRENCES = 366;

function batchRangeValidator(
  batchStart?: string,
  batchEnd?: string
): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const start = group.get('startDate')?.value;
    const end = group.get('endDate')?.value;
    if (
      (batchStart && start && start < batchStart) ||
      (batchEnd && end && end > batchEnd)
    ) {
      return { batchRange: true };
    }
    return null;
  };
}

function dateTimeValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const start = group.get('startDate')?.value;
    const end = group.get('endDate')?.value;
    if (start && end && new Date(end) < new Date(start)) {
      return { dateRange: true };
    }
    const startTime = group.get('startTime')?.value;
    const endTime = group.get('endTime')?.value;
    if (startTime && endTime && start === end && endTime <= startTime) {
      return { timeRange: true };
    }
    return null;
  };
}

@Component({
  selector: 'app-class-form',
  templateUrl: './class-form.component.html',
  styleUrls: ['./class-form.component.scss']
})
export class ClassFormComponent implements OnInit {
  form: FormGroup;
  tutors: any[] = [];
  isEditMode: boolean;
  saving = false;

  weekDays = [
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
    { value: 0, label: 'Sun' }
  ];

  repeatIntervals = [
    { value: 2, label: 'Alternate weeks' },
    { value: 3, label: 'Every 3 weeks' },
    { value: 4, label: 'Every 4 weeks' }
  ];

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ClassFormComponent, ClassFormResult>,
    private globalApi: GlobalApiService,
    private notifier: NotifierService,
    @Inject(MAT_DIALOG_DATA) public data: ClassFormDialogData
  ) {
    this.isEditMode = !!data?.pot;
    const source = data?.pot ?? data?.duplicateOf;

    this.form = this.fb.group(
      {
        title: [source?.title ?? '', Validators.required],
        startDate: [data.startDate ?? source?.startDate, Validators.required],
        endDate: [
          data.endDate ?? source?.endDate ?? data.startDate,
          Validators.required
        ],
        startTime: [source?.startTime ?? data.startTime ?? ''],
        endTime: [source?.endTime ?? data.endTime ?? ''],
        location: [source?.location ?? ''],
        isActive: [source?.isActive ?? true],
        hideDetails: [source?.hideDetails ?? false],
        type: [source?.type ?? null, Validators.required],
        subject: [source?.subject ?? null, Validators.required],
        tutor: [source?.tutor ?? null, Validators.required],
        timeZone: [source?.timeZone ?? null, Validators.required],
        repeatDays: [[] as number[]],
        repeatIntervalWeeks: [1]
      },
      {
        validators: [
          dateTimeValidator(),
          batchRangeValidator(data.batchStartDate, data.batchEndDate),
          this.overlapValidator()
        ]
      }
    );

    // The event API keeps the batch time zone, so it can't be changed here
    if (this.isClassEventEdit) {
      this.form.get('timeZone')?.disable();
      this.form.get('startTime')?.addValidators(Validators.required);
      this.form.get('endTime')?.addValidators(Validators.required);
      // A class is a single day, so the hidden end date follows the date
      this.form.get('startDate')?.valueChanges.subscribe((value: string) => {
        this.form.get('endDate')?.setValue(value);
      });
    }

    this.form.get('startTime')?.valueChanges.subscribe((value: string) => {
      if (value && !this.form.get('endTime')?.value) {
        this.form
          .get('endTime')
          ?.setValue(addMinutesToTime(value, 60), { emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.loadTutors();
  }

  get f() {
    return this.form.controls;
  }

  get isClassEventEdit(): boolean {
    return !!this.data?.classEventEditType && this.data?.pot?.bseId != null;
  }

  // "Don't show schedule" is per day, so only for a single saved class
  get canHideDetails(): boolean {
    return this.isClassEventEdit && this.data.classEventEditType === 1;
  }

  // The Monday–Sunday week (YYYY-MM-DD) of the class being edited
  get classWeek(): { start: string; end: string } | null {
    return this.isClassEventEdit ? classWeekOf(this.data.pot!.startDate) : null;
  }

  get classWeekLabel(): string {
    return this.isClassEventEdit
      ? formatClassWeek(this.data.pot!.startDate)
      : '';
  }

  // Explains which classes the edit changes
  get editScopeHint(): string | null {
    const { clickedDate, seriesRange } = this.data;
    const label = (date: string) => moment(date, 'YYYY-MM-DD').format('ddd, MMM D');
    if (this.isClassEventEdit) {
      if (this.data.classEventEditType !== 2 || !seriesRange) {
        return null;
      }
      const classes = `${seriesRange.count} class${seriesRange.count === 1 ? '' : 'es'}`;
      return seriesRange.start === seriesRange.end
        ? `Applies to ${label(seriesRange.start)} (${classes})`
        : `Applies to ${label(seriesRange.start)} – ${label(seriesRange.end)} (${classes})`;
    }
    if (this.isEditMode && clickedDate) {
      return `You clicked ${label(clickedDate)}. Changes apply to all classes in this schedule.`;
    }
    return null;
  }

  get batchRangeLabel(): string {
    const { batchStartDate, batchEndDate } = this.data;
    return batchStartDate && batchEndDate
      ? `${formatDateLabel(batchStartDate)} – ${formatDateLabel(batchEndDate)}`
      : '';
  }

  get durationLabel(): string | null {
    return formatDuration(this.f['startTime'].value, this.f['endTime'].value);
  }

  // The class time in the viewer's own time zone, when that differs
  get viewerTimeHint(): string | null {
    const date = this.f['startDate'].value;
    const startTime = this.f['startTime'].value;
    const endTime = this.f['endTime'].value;
    const timeZone = this.f['timeZone'].value;
    if (!date || !startTime || !endTime || !timeZone) {
      return null;
    }
    const start = toViewerTime(date, startTime, timeZone);
    if (
      start.format('YYYY-MM-DD HH:mm') === `${date} ${startTime}`
    ) {
      return null;
    }
    const endDate = endTime <= startTime ? addDaysUtc(date, 1) : date;
    const end = toViewerTime(endDate, endTime, timeZone);
    return `In your time zone: ${start.format('ddd D MMM, h:mm A')} – ${end.format(
      'h:mm A'
    )} ${viewerTimeZoneAbbr(start)}`;
  }

  get isPast(): boolean {
    const startDate = this.f['startDate'].value;
    if (!startDate) {
      return false;
    }
    return isPastDateTime(startDate, this.f['startTime'].value || undefined);
  }

  get repeatFrequencyHint(): string {
    const weeks = this.f['repeatIntervalWeeks'].value;
    const days: number[] = this.f['repeatDays'].value ?? [];
    const start = this.f['startDate'].value;
    if (!weeks || weeks <= 1 || !days.length || !start) {
      return `Repeats every ${weeks || 1} week(s) on the selected days`;
    }
    const until = addDaysUtc(start, weeks * 7 + 7);
    const next = generateWeeklyOccurrences(start, until, days, 2, weeks)[1];
    return next
      ? `Repeats every ${weeks} weeks — next on ${formatDateLabel(next)}`
      : `Repeats every ${weeks} weeks on the selected days`;
  }

  isRepeatDaySelected(day: number): boolean {
    return (this.form.get('repeatDays')?.value ?? []).includes(day);
  }

  toggleRepeatDay(day: number, checked: boolean): void {
    const control = this.form.get('repeatDays');
    const current: number[] = control?.value ?? [];
    const next = checked ? [...current, day] : current.filter((d) => d !== day);
    control?.setValue(next);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.value;

    if (this.isClassEventEdit) {
      this.saveClassEvent(value);
      return;
    }

    this.dialogRef.close({
      saved: true,
      schedules: this.buildSchedules(value)
    });
  }

  private saveClassEvent(value: any): void {
    if (this.saving) {
      return;
    }
    this.saving = true;
    this.globalApi
      .updateBatchScheduleEvent({
        bseId: this.data.pot!.bseId!,
        editType: this.data.classEventEditType!,
        title: value.title.trim(),
        classDate: moment(value.startDate, 'YYYY-MM-DD').format('MM-DD-YYYY'),
        startTime: formatTime12Hour(value.startTime),
        endTime: formatTime12Hour(value.endTime),
        learningType: value.type,
        subject: value.subject,
        tutor: value.tutor,
        classLink: value.location || '',
        // Left out of "this and following" edits, so each class keeps its own
        ...(this.canHideDetails && { hideDetails: value.hideDetails ? 1 : 0 })
      })
      .subscribe({
        next: () => {
          this.saving = false;
          this.dialogRef.close({ saved: true, schedules: [] });
        },
        error: (error) => {
          this.saving = false;
          this.notifier.notify('error', this.classEventErrorMessage(error));
        }
      });
  }

  // A readable message for the event API's error codes
  private classEventErrorMessage(error: any): string {
    switch (error?.error?.msg) {
      case 'class_date_outside_selected_week':
        // Shown under the date field until the date is changed
        this.f['startDate'].setErrors({ outsideWeek: true });
        this.f['startDate'].markAsTouched();
        return `A class can only be moved to another day in its own week (${this.classWeekLabel}).`;
      default:
        return 'Unable to update the class';
    }
  }

  private buildSchedules(value: any): ClassFormSchedule[] {
    const days: number[] = value.repeatDays?.length
      ? value.repeatDays
      : [getUtcDay(value.startDate)];

    const schedules: ClassFormSchedule[] = days.map((day) => ({
      bschId: null,
      title: value.title.trim(),
      scheduleStartDate: value.startDate,
      scheduleEndDate: value.endDate,
      type: value.type,
      subject: value.subject,
      tutor: value.tutor,
      day,
      startTime: value.startTime ? formatTime12Hour(value.startTime) : '',
      endTime: value.endTime ? formatTime12Hour(value.endTime) : '',
      timeZone: value.timeZone,
      classUrl: value.location || '',
      isScheduleActive: 1,
      isDisabled: value.isScheduleActive ? 0 : 1,
      repeatFrequency: value.repeatIntervalWeeks || 1
    }));

    console.log('Class form schedules:', schedules);
    return schedules;
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private loadTutors(): void {
    this.globalApi.getAllTutors().subscribe({
      next: (response: any) => {
        const tutors = JSON.parse(response['data']);
        for (let i = 0; i < tutors.length; i++) {
          tutors[i]['tutorName'] =
            tutors[i]['first_name'] + ' ' + tutors[i]['last_name'];
        }
        this.tutors = tutors;
      },
      error: () => {
        this.notifier.notify('error', 'Unable to fetch tutors');
      }
    });
  }

  private generateOccurrenceDates(
    startDate: string,
    endDate: string,
    repeatDays: number[],
    intervalWeeks = 1
  ): string[] {
    if (!repeatDays?.length) {
      return [startDate];
    }
    return generateWeeklyOccurrences(
      startDate,
      endDate,
      repeatDays,
      MAX_OCCURRENCES,
      intervalWeeks
    );
  }

  private overlapValidator(): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startDate = group.get('startDate')?.value;
      const endDate = group.get('endDate')?.value;
      const tutor = group.get('tutor')?.value;
      if (!tutor || !startDate || !endDate) {
        return null;
      }
      const startTime = group.get('startTime')?.value || undefined;
      const endTime = group.get('endTime')?.value || undefined;
      const timeZone = group.get('timeZone')?.value;
      // Classes that run past midnight end on the next day
      const crossesMidnight = !!startTime && !!endTime && endTime <= startTime;
      const dates = this.generateOccurrenceDates(
        startDate,
        endDate,
        group.get('repeatDays')?.value ?? [],
        group.get('repeatIntervalWeeks')?.value
      );

      for (const date of dates) {
        const endDate = crossesMidnight ? addDaysUtc(date, 1) : date;
        // Booked classes are in the viewer's time zone, so compare there
        const start = startTime && toViewerTime(date, startTime, timeZone);
        const end = endTime && toViewerTime(endDate, endTime, timeZone);
        const conflict = findOverlappingClass(
          {
            tutor,
            startDate: start ? start.format('YYYY-MM-DD') : date,
            endDate: end ? end.format('YYYY-MM-DD') : endDate,
            startTime: start ? start.format('HH:mm') : undefined,
            endTime: end ? end.format('HH:mm') : undefined
          },
          this.data.bookedClasses ?? []
        );
        if (conflict) {
          return { overlap: { title: conflict.title, date } };
        }
      }
      return null;
    };
  }
}
